import { addDays, addWeeks, endOfWeek, format, isSameDay, startOfWeek } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import CompletedVisitCard from 'src/components/CompletedVisitCard';
import MainNav, { MobileNavHeight } from 'src/components/MainNav';
import StyledButtonWithStartIcon from 'src/components/StyledButtonWithStartIcon';
import StyledCardContent from 'src/components/StyledCardContent';
import StyledIconButton from 'src/components/StyledIconButton';
import TopLevelPageHeader from 'src/components/TopLevelPageHeader';
import UpcomingVisitCard from 'src/components/VisitCard';
import EditVisitsModal from 'src/components/modals/EditVisitsModal';
import { useModalManager } from 'src/components/modals/ModalManager';
import { TeamRoles } from 'src/constants/TeamRoles';
import { VisitStatuses } from 'src/constants/Visit';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import useResponsive from 'src/hooks/useResponsive';
import { useScroll } from 'src/hooks/useScroll';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { useVisits } from 'src/hooks/useVisits';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { type Visit } from 'src/store/sageHomeApi';

const visitsSearchSchema = z.object({
    weekOffset: z.coerce.number().default(0),
});

export const Route = createFileRoute('/(app)/visits/')({
    component: VisitsComponent,
    validateSearch: zodValidator(visitsSearchSchema),
});

const PageName = 'Visits Page';

function VisitsComponent() {
    const { isMobile } = useResponsive();
    const navigate = useNavigate();
    const { spacing, shadows, zIndex } = useTheme();
    // Get week offset from URL query params (0 = current week, -1 = previous week, 1 = next week, etc.)
    const { weekOffset } = Route.useSearch();

    const { trackPageView } = useAnalytics();
    const { openModal } = useModalManager();

    // TODO: find a better way to handle the scroll threshold
    const { isScrolled } = useScroll({ enabled: isMobile, threshold: parseInt(spacing(7.5)) });
    const { currentUser } = useTeamMemberData();
    const canEditVisits =
        currentUser?.isTeamAdmin ||
        currentUser?.role === TeamRoles.TeamAdmin ||
        currentUser?.role === TeamRoles.CareRecipient;

    // Calculate date range based on current week (Monday to Sunday) with offset
    const { startInstant, endInstant } = useMemo(() => {
        const today = new Date();
        const weekStartDate = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), weekOffset);
        const weekEndDate = addWeeks(endOfWeek(today, { weekStartsOn: 1 }), weekOffset);
        return {
            startInstant: weekStartDate.getTime(),
            endInstant: weekEndDate.getTime(),
        };
    }, [weekOffset]);

    const {
        visits = [],
        isLoading,
        error,
    } = useVisits({
        startInstant,
        endInstant,
    });

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Generate all days in the current week (Monday to Sunday)
    const weekDays = useMemo(() => {
        const today = new Date();
        const weekStart = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), weekOffset);
        return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    }, [weekOffset]);

    // Group visits by day
    const visitsByDay = useMemo(() => {
        // Initialize all days with empty arrays
        const grouped = Object.fromEntries(
            weekDays.map((day) => [format(day, 'yyyy-MM-dd'), []] as [string, Visit[]])
        );

        // Group visits by day
        return visits.reduce((acc, visit) => {
            if (visit.visitStartInstant) {
                const dayKey = format(new Date(visit.visitStartInstant), 'yyyy-MM-dd');
                if (acc[dayKey]) {
                    acc[dayKey].push(visit);
                }
            }
            return acc;
        }, grouped);
    }, [visits, weekDays]);

    // Calculate week label based on offset
    const weekLabel = useMemo(() => {
        if (weekOffset === 0) {
            return 'This Week';
        } else if (weekOffset === -1) {
            return 'Last Week';
        } else if (weekOffset === 1) {
            return 'Next Week';
        } else {
            // For other weeks, show date range
            const weekStart = weekDays[0];
            const weekEnd = weekDays[6];
            return `Week of ${format(weekStart, 'M/d')} - ${format(weekEnd, 'M/d')}`;
        }
    }, [weekOffset, weekDays]);

    const handlePreviousWeek = () => {
        navigate({ to: '/visits', search: { weekOffset: weekOffset - 1 } });
    };

    const handleNextWeek = () => {
        navigate({ to: '/visits', search: { weekOffset: weekOffset + 1 } });
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [weekOffset]);

    return (
        <InnerMaxWidthContainer>
            <TopLevelPageHeader pageTitle="Visits" />

            {error && (
                <Stack>
                    <Typography color="error" variant="body1">
                        Error loading visits. Please try again.
                    </Typography>
                </Stack>
            )}
            <Stack sx={{ gap: 2, mb: isMobile ? 6 : 0 }}>
                {/* Week Navigation */}
                <Stack
                    direction="row"
                    sx={{
                        position: isMobile ? 'sticky' : 'relative',
                        ...(isMobile && {
                            // Break out of container padding to extend to screen edges
                            // Container has px: 2.5, so we need to extend by 2.5 on each side
                            width: `calc(100% + ${spacing(5)})`, // 2.5 * 2 = 5 for both sides
                            marginLeft: `calc(-1 * ${spacing(2.5)})`,
                            marginRight: `calc(-1 * ${spacing(2.5)})`,
                            // Add padding to match container's inner content spacing
                            px: spacing(2.5),
                        }),
                        top: 0,
                        minHeight: spacing(7.5),
                        background: isMobile && isScrolled ? 'white' : 'transparent',
                        boxShadow: isMobile && isScrolled ? shadows[2] : 'none',
                        transition:
                            'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',

                        mb: 2.5,
                        py: 2.5,
                        zIndex: zIndex.appBar,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <StyledIconButton
                        icon={faChevronLeft}
                        onClick={handlePreviousWeek}
                        variant="light"
                    />

                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {weekLabel}
                    </Typography>
                    <StyledIconButton
                        icon={faChevronRight}
                        onClick={handleNextWeek}
                        variant="light"
                    />
                </Stack>

                <Stack sx={{ gap: 3 }}>
                    {weekDays.map((day, index) => {
                        const dayKey = format(day, 'yyyy-MM-dd');
                        const dayVisits = visitsByDay[dayKey] || [];
                        const today = new Date();
                        const isToday = isSameDay(day, today);

                        // Format day heading: "Today" or "Tuesday 11/11"
                        const dayHeading = isToday ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Chip
                                    label="Today"
                                    sx={{
                                        backgroundColor: 'purple.400',
                                        color: 'common.white',
                                        '& .MuiChip-label': {
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: 'purple.900' }}
                                >
                                    {format(day, 'M/d')}
                                </Typography>
                            </Stack>
                        ) : (
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'purple.900' }}
                            >{`${format(day, 'EEEE M/d')}`}</Typography>
                        );

                        return (
                            <Stack sx={{ gap: 2 }} key={`${dayKey}-index`}>
                                {dayHeading}
                                {isLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height={60}
                                        sx={{ borderRadius: 3 }}
                                    />
                                ) : dayVisits.length === 0 ? (
                                    <Card sx={{ backgroundColor: 'gray.300', boxShadow: 'none' }}>
                                        <StyledCardContent>
                                            <Typography variant="body2" color="gray.900">
                                                No visit scheduled
                                            </Typography>
                                        </StyledCardContent>
                                    </Card>
                                ) : (
                                    <Stack sx={{ gap: 2 }} key={index}>
                                        {dayVisits.map((visit: Visit, index: number) =>
                                            visit.visitStatus === VisitStatuses.COMPLETED ? (
                                                <CompletedVisitCard
                                                    visit={visit}
                                                    key={`${visit.visitRid}-${index}`}
                                                />
                                            ) : (
                                                <UpcomingVisitCard
                                                    visit={visit}
                                                    showDate={false}
                                                    key={`${visit.visitRid}-${index}`}
                                                />
                                            )
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
            </Stack>

            {canEditVisits && (
                <StyledButtonWithStartIcon
                    variant="contained"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPencil} />}
                    onClick={() => openModal({ content: EditVisitsModal })}
                    sx={{
                        position: 'fixed',
                        bottom: isMobile
                            ? `${MobileNavHeight + parseInt(spacing(2))}px`
                            : spacing(2),
                        right: isMobile ? '50%' : spacing(2),
                        transform: isMobile ? 'translateX(50%)' : 'none',
                    }}
                >
                    Edit Visits
                </StyledButtonWithStartIcon>
            )}

            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    );
}
